import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sharedApi } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import Seo from "@/components/common/Seo";
import { Loader2, CheckCircle2, XCircle, Sparkles, Package } from "lucide-react";

/**
 * Stripe return page for Paid Boost.
 * Spec: SPEC_PAID_BOOST_ALL_PODS — poll GET /classifieds/boost/status/{session_id}
 * until `payment_status` becomes 'paid' or 'unpaid' (or after 10 attempts, give up).
 */
export default function BoostReturnPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [attempts, setAttempts] = useState(0);
  const [pending, setPending] = useState(() => {
    try {
      const raw = sessionStorage.getItem("boost_pending");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const q = useQuery({
    queryKey: ["boost-status", sessionId, attempts],
    queryFn: async () => {
      const { data } = await sharedApi.get(`/classifieds/boost/status/${sessionId}`);
      return data;
    },
    enabled: !!sessionId,
    retry: 0,
  });

  const status = q.data?.payment_status;
  const order = q.data?.order;
  const isPaid = status === "paid";
  const isFailed = status === "unpaid" || status === "expired" || status === "canceled";
  const isPolling = !isPaid && !isFailed && attempts < 10;

  // Poll every 1.5s up to 10 attempts
  useEffect(() => {
    if (!sessionId) return;
    if (!isPolling) return;
    const t = setTimeout(() => setAttempts((a) => a + 1), 1500);
    return () => clearTimeout(t);
  }, [sessionId, isPolling, attempts]);

  // Clear the pending pointer once resolved
  useEffect(() => {
    if (isPaid || isFailed) {
      try { sessionStorage.removeItem("boost_pending"); } catch { /* ignore */ }
    }
  }, [isPaid, isFailed]);

  const featuredUntilLabel = useMemo(() => {
    const iso = order?.featured_until_after || order?.featured_until;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
  }, [order]);

  if (!sessionId) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <XCircle className="w-10 h-10 text-red-500 mx-auto" />
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">Missing session</h1>
        <p className="mt-2 text-slate-500">We couldn&apos;t find the boost session — please try again from My ads.</p>
        <button
          type="button"
          onClick={() => nav("/my-ads")}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
        >
          Go to My ads
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="boost-return-page">
      <Seo title="Boost payment" description="Confirming your Paid Boost checkout." />

      {isPolling && (
        <>
          <Loader2 className="w-10 h-10 text-[color:var(--brand-blue)] mx-auto animate-spin" />
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-900" data-testid="boost-return-checking">
            Confirming your payment…
          </h1>
          <p className="mt-2 text-slate-500 text-sm">This usually takes a second or two.</p>
        </>
      )}

      {isPaid && (
        <div data-testid="boost-return-success">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-black text-slate-900">
            Your ad is featured!
          </h1>
          {featuredUntilLabel ? (
            <p className="mt-3 text-slate-600 leading-relaxed">
              {pending?.title ? <span className="font-semibold text-slate-900">&ldquo;{pending.title}&rdquo;</span> : "Your ad"} is on the homepage until <span className="font-semibold text-slate-900">{featuredUntilLabel}</span>. 🎉
            </p>
          ) : (
            <p className="mt-3 text-slate-600">Your boost is now active.</p>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              data-testid="boost-return-home"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold text-sm px-5 py-2.5 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> See it on the homepage
            </Link>
            <Link
              to="/my-ads"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm px-5 py-2.5 transition-colors"
            >
              <Package className="w-4 h-4" /> Back to My ads
            </Link>
          </div>
        </div>
      )}

      {(isFailed || (!isPaid && !isPolling)) && (
        <div data-testid="boost-return-failed">
          <XCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">
            Payment didn&apos;t go through
          </h1>
          <p className="mt-2 text-slate-500 max-w-sm mx-auto">
            No charge was made. You can try again from My ads, or contact us if the issue persists.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/my-ads"
              data-testid="boost-return-retry"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
            >
              Back to My ads
            </Link>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm px-5 py-2.5 transition-colors"
            >
              Contact us
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
